import { supabase } from '../lib/supabase';

export const messagesService = {
  // Get or create a conversation between buyer and seller for a listing
  async getOrCreateConversation(listingId, buyerId, sellerId) {
    try {
      console.log('Getting/creating conversation:', { listingId, buyerId, sellerId });
      
      // First try to find existing conversation
      const { data: existingConv, error: findError } = await supabase
        .from('conversations')
        .select('*')
        .eq('listing_id', listingId)
        .eq('buyer_id', buyerId)
        .eq('seller_id', sellerId)
        .single();

      console.log('Existing conversation search:', { existingConv, findError });

      if (existingConv) {
        console.log('Found existing conversation:', existingConv);
        return existingConv;
      }

      // If not found, create new conversation
      console.log('Creating new conversation...');
      const { data: newConv, error: createError } = await supabase
        .from('conversations')
        .insert([{
          listing_id: listingId,
          buyer_id: buyerId,
          seller_id: sellerId
        }])
        .select()
        .single();

      console.log('New conversation creation:', { newConv, createError });

      if (createError) {
        console.error('Error creating conversation:', createError);
        throw createError;
      }
      
      return newConv;
    } catch (error) {
      console.error('Error getting/creating conversation:', error);
      throw error;
    }
  },

  // Get all conversations for the current user
  async getConversations(userId) {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  // Get messages for a specific conversation
  async getMessages(conversationId) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Supabase messages error:', error);
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  // Send a message
  async sendMessage(conversationId, senderId, recipientId, content) {
    try {
      console.log('Sending message to Supabase:', {
        conversationId,
        senderId,
        recipientId,
        content
      });

      const { data, error } = await supabase
        .from('messages')
        .insert([{
          conversation_id: conversationId,
          sender_id: senderId,
          recipient_id: recipientId,
          content: content
        }])
        .select()
        .single();

      console.log('Message insert response:', { data, error });

      if (error) {
        console.error('Supabase message insert error:', error);
        throw error;
      }

      // Update conversation's last_message_at
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', conversationId);

      if (updateError) {
        console.error('Error updating conversation timestamp:', updateError);
      }

      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  // Mark messages as read
  async markAsRead(messageIds) {
    try {
      console.log('📖 Marking messages as read:', messageIds);
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .in('id', messageIds);

      if (error) throw error;
      
      // Refresh unread count after marking as read
      if (window.refreshUnreadCount) {
        console.log('🔄 Calling refreshUnreadCount...');
        setTimeout(() => window.refreshUnreadCount(), 500);
      }
      
      return true;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  },

  // Subscribe to new messages for a conversation
  subscribeToMessages(conversationId, callback) {
    console.log('Creating subscription for conversation:', conversationId);
    
    return supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          console.log('Supabase subscription payload:', payload);
          callback(payload);
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });
  },

  // Subscribe to conversation updates
  subscribeToConversations(userId, callback) {
    return supabase
      .channel(`conversations:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `or(buyer_id.eq.${userId},seller_id.eq.${userId})`
        },
        callback
      )
      .subscribe();
  }
};
